import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppWindow, ChevronDown, MoreHorizontal } from 'lucide-react';
import { TopicPlanItems } from '@/components/activity/TopicPlanItems';
import { DoneLine, type RowPlan } from '@/components/activity/LivingWorkRow';
import type { ThreadTopicSummary } from '@shared/types/thread-work';
import { THREAD_VISIBLE_STATE_LABELS } from '@/components/activity/thread-living-model';
import { RowIdentity, TopicStatusIcon } from '@/components/activity/work-row-visuals';
import { DENSE_ACTION_CLASS } from '@/components/work/WorkActionControl';
import { sweepBusinessVocabulary, sweepSerializedBusinessText } from '@/lib/ui-editorial-sweeper';
import { AppTaskProposal } from './AppTaskProposal';
import { AppToolProposalLink } from './AppToolProposalLink';

/** One task of an app's own work (outside any account) in Mon travail: the
 * app in the first cell, as an account would be. Same actions as the app page:
 * a ready proposal is launched here, anything else opens the app. */
export const AppWorkRow = ({ flowId, flowTitle, topic, onChange, plan }: {
  flowId: string;
  flowTitle: string;
  topic: ThreadTopicSummary;
  onChange?: () => void;
  plan?: RowPlan;
}) => {
  const [contextOpen, setContextOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const contextId = useId();
  const appHref = `/tools/${encodeURIComponent(flowId)}`;
  const title = sweepBusinessVocabulary(sweepSerializedBusinessText(plan?.title ?? topic.title));
  const reason = topic.proposal?.rationale ?? topic.decision?.reason ?? null;
  const proposal = topic.visibleState === 'actionable' && topic.proposal?.status === 'active'
    ? topic.proposal : null;
  const command = proposal?.executorKind === 'tool'
    ? <AppToolProposalLink
        topic={topic}
        proposal={proposal}
        label="Lancer"
        className={`inline-flex min-h-8 max-w-full items-center rounded-lg px-3 py-1 text-xs font-semibold leading-4 ${DENSE_ACTION_CLASS}`}
      />
    : proposal && (proposal.executorKind === 'recipe' || proposal.executorKind === 'capability')
    ? <AppTaskProposal topic={topic} proposal={proposal} onDone={() => onChange?.()} label="Lancer" dense />
    : <Link to={appHref} className="text-sm font-semibold text-domino-aubergine underline underline-offset-4">Ouvrir</Link>;

  return (
    <article className="rounded-lg border border-domino-cardBorder bg-domino-white px-3.5 py-3 transition-colors hover:bg-domino-muted/30 sm:rounded-none sm:border-0">
      <div data-work-action={topic.id} className="work-action-row">
        <div className="work-account min-w-0">
          {!plan?.nested && <RowIdentity icon={AppWindow} label={flowTitle} href={appHref} detail={(topic.visibleState !== 'actionable' || plan?.note) && (
            <>
              {topic.visibleState !== 'actionable' && <><TopicStatusIcon state={topic.visibleState} />{THREAD_VISIBLE_STATE_LABELS[topic.visibleState]}</>}
              {plan?.note && <span className="tabular-nums">{topic.visibleState !== 'actionable' ? '· ' : ''}{plan.note}</span>}
            </>
          )} />}
        </div>
        <div className="work-menu">
          {plan && (
            <div className="relative shrink-0">
              <button
                type="button"
                aria-label={`Options pour ${title}`}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((current) => !current)}
                className="inline-flex h-8 w-8 items-center justify-center rounded text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-9 z-20 w-56 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-elevated)]">
                  <TopicPlanItems topic={topic} commitment={plan.commitment} bucket={plan.bucket} onDone={() => { setMenuOpen(false); onChange?.(); }} />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="work-title flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1">
            <h3
              title={title}
              className={`text-sm font-normal leading-5 text-domino-ink ${contextOpen ? '' : 'line-clamp-2'}`}
            >
              {title}
            </h3>
            {plan?.done && <DoneLine {...plan.done} />}
            {plan?.reply && <p title={plan.reply} className="mt-1 line-clamp-2 text-xs leading-5 text-domino-aubergine">{plan.reply}</p>}
          </div>
          {reason && (
            <button
              type="button"
              aria-expanded={contextOpen}
              aria-controls={contextId}
              onClick={() => setContextOpen((open) => !open)}
              title="Voir le contexte"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-domino-inkSoft hover:bg-domino-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
            >
              <span className="sr-only">Voir le contexte</span>
              <ChevronDown className={`h-4 w-4 transition ${contextOpen ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
        <div className="work-command min-w-0">{command}</div>
        {contextOpen && reason && (
          <div id={contextId} className="col-span-full border-t border-domino-cardBorder py-3">
            <p className="max-w-3xl text-sm leading-6 text-domino-inkSoft">{sweepBusinessVocabulary(reason)}</p>
          </div>
        )}
      </div>
    </article>
  );
};
